import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PersonChip } from "@/components/MappingPersonRow";
import { UserPicker } from "@/components/UserPicker";
import type { TesterMapping } from "@/hooks/useWizardPersistence";
import type { TesterUser } from "@/services/testers";

interface FlatTableFallbackProps {
  mappings: TesterMapping[];
  scenarioStepKeys: Set<string>;
  cognitoUsers: TesterUser[];
  isTestersLoading: boolean;
  isTestersError: boolean;
  onResolve: (mappingId: string, personIndex: number, user: TesterUser) => void;
  onRemove: (mappingId: string, personIndex: number) => void;
  onAddUser: (mappingId: string, user: TesterUser) => void;
  onRetryTesters: () => void;
}

export function FlatTableFallback({
  mappings,
  scenarioStepKeys,
  cognitoUsers,
  isTestersLoading,
  isTestersError,
  onResolve,
  onRemove,
  onAddUser,
  onRetryTesters,
}: FlatTableFallbackProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px]">Domain</TableHead>
          <TableHead className="w-[160px]">Tester</TableHead>
          <TableHead className="w-[80px]">Area</TableHead>
          <TableHead>Person(s)</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {mappings.map((mapping) => (
          <TableRow key={mapping.id}>
            <TableCell>
              <Badge variant="secondary" className="text-xs">
                {mapping.domain || "N/A"}
              </Badge>
            </TableCell>
            <TableCell className="font-medium text-sm">{mapping.tester}</TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-xs">{mapping.area}</Badge>
                {scenarioStepKeys.size > 0 &&
                  !scenarioStepKeys.has(`${mapping.tester.toLowerCase()}|${mapping.area.toLowerCase()}`) && (
                  <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] px-1">
                    <AlertTriangle className="mr-0.5 h-3 w-3" />No match
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center gap-1.5">
                {mapping.testPersons.map((person, pIdx) => (
                  <PersonChip
                    key={`${mapping.id}-${pIdx}`}
                    person={person}
                    mappingId={mapping.id}
                    personIndex={pIdx}
                    cognitoUsers={cognitoUsers}
                    isTestersLoading={isTestersLoading}
                    isTestersError={isTestersError}
                    onResolve={onResolve}
                    onRemove={onRemove}
                    onRetryTesters={onRetryTesters}
                  />
                ))}
                <UserPicker
                  mode="add"
                  onSelect={(u) => onAddUser(mapping.id, u)}
                  excludeUserIds={mapping.testPersons.map(p => p.cognitoUserId).filter(Boolean) as string[]}
                  cognitoUsers={cognitoUsers}
                  isLoading={isTestersLoading}
                  isError={isTestersError}
                  onRetry={onRetryTesters}
                />
                {mapping.testPersons.length === 0 && (
                  <span className="text-xs text-warning">Add at least one</span>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
